import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AdminSliceState {
  showHidden: boolean;
  showInherited: boolean;
  showDisabled: boolean;
  showArchived: boolean;

  useUtcTime: boolean;
  use24HourFormat: boolean;
}

// Helper functions for localStorage
const loadBooleanFromStorage = (key: string, defaultValue: boolean): boolean => {
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveBooleanToStorage = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if localStorage is not available
  }
};

const initialState: AdminSliceState = {
  showHidden: loadBooleanFromStorage('admin_show_hidden', false),
  showInherited: loadBooleanFromStorage('admin_show_inherited', true),
  showDisabled: loadBooleanFromStorage('admin_show_disabled', true),
  showArchived: loadBooleanFromStorage('admin_show_archived', false),
  useUtcTime: loadBooleanFromStorage('admin_use_utc_time', false),
  use24HourFormat: loadBooleanFromStorage('admin_use_24_hour_format', true)
};

export const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setUseUtcTime: (state, action: PayloadAction<boolean>) => {
      state.useUtcTime = action.payload;
      saveBooleanToStorage('admin_use_utc_time', action.payload);
    },
    setUse24HourFormat: (state, action: PayloadAction<boolean>) => {
      state.use24HourFormat = action.payload;
      saveBooleanToStorage('admin_use_24_hour_format', action.payload);
    },
    setShowHidden: (state, action: PayloadAction<boolean>) => {
      state.showHidden = action.payload;
      saveBooleanToStorage('admin_show_hidden', action.payload);
    },
    setShowInherited: (state, action: PayloadAction<boolean>) => {
      state.showInherited = action.payload;
      saveBooleanToStorage('admin_show_inherited', action.payload);
    },
    setShowDisabled: (state, action: PayloadAction<boolean>) => {
      state.showDisabled = action.payload;
      saveBooleanToStorage('admin_show_disabled', action.payload);
    },
    setShowArchived: (state, action: PayloadAction<boolean>) => {
      state.showArchived = action.payload;
      saveBooleanToStorage('admin_show_archived', action.payload);
    }
  }
});

export const { setUseUtcTime, setUse24HourFormat, setShowHidden, setShowInherited, setShowDisabled, setShowArchived } = slice.actions;
export default slice.reducer;
